import React from "react";
import { Download } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { INFRA_ITEMS, CRITERIA } from "@/mock/data";
import { TextField, SelectField, TextareaField, FileField, YesNo, Fieldset, Criteria, QuestionCard } from "@/components/fields/Field";
import { openPdf } from "@/lib/openPdf";

// ===================== STEP 4 : Infrastructure =====================
export function Step4({ errors = {} }) {
  const { application, removeFields } = useApp();
  return (
    <div className="animate-fade-up">
      <Criteria>{CRITERIA[4]}</Criteria>
      <p className="mb-4 -mt-2 text-[13.5px] text-slate-600">
        प्रत्येक सुविधेसाठी "आहे / नाही" निवडा. "आहे" निवडल्यास <strong>तपशील व पुरावा अपलोड</strong> देणे आवश्यक आहे.
      </p>
      <div>
        <QuestionCard index={0} number={1} title="प्रस्तावित कार्यालयाच्या जागेचा पत्ता" required
          note="किमान ६ दशांश स्थळांपर्यंत अचूक अक्षांश/रेखांश प्रविष्ट करा. (उदा. 16.852345)">
          <TextareaField label="पत्ता" name="office_address" rows={3} required errors={errors} />
          <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextField label="अक्षांश (Latitude)" name="office_lat" required errors={errors} />
            <TextField label="रेखांश (Longitude)" name="office_lng" required errors={errors} />
          </div>
        </QuestionCard>
        {INFRA_ITEMS.map((item, i) => {
          const yes = application.data[`infra_${i}`] === "yes";
          return (
            <QuestionCard key={i} index={i + 1} number={i + 2} title={item.label} required>
              <div className={`grid grid-cols-1 items-start gap-4 ${(i === 2) ? "md:grid-cols-[auto_1fr_2fr]" : "md:grid-cols-[auto_1fr]"}`}>
                <YesNo name={`infra_${i}`} onToggle={(v) => { if (v === "no") removeFields((k) => k === `infra_qty_${i}` || k === `infra_file_${i}`); }} />
                <div className={yes ? "" : "pointer-events-none"}>
                  <TextField label={item.qtyLabel} name={`infra_qty_${i}`} required={yes} errors={errors} />
                </div>
                {(i === 2) && (
                  <div className={yes ? "" : "pointer-events-none"}>
                    <FileField label="पुरावा दस्तऐवज / फोटो" name={`infra_file_${i}`} required={yes} errors={errors} compact />
                  </div>
                )}
              </div>
            </QuestionCard>
          );
        })}
      </div>
    </div>
  );
}

// ===================== STEP 5 : Financial capacity =====================
export function Step5({ errors = {} }) {
  const { application, removeFields } = useApp();
  const d = application.data;
  const blocks = [
    "गुंतवणुकीसाठी पुरेशी बँक शिल्लक/मुळात ठेव (FD) व इतर गुंतवणुकीबाबत (दि. 01/04/2026 पासून दि. 31/08/2026 पर्यंतचे बँक स्टेटमेंट -कोणत्याही एका महिन्याचे किमान र.रु. 20,000/- शिल्लक किंवा  दि. 31/08/2026 पूर्वीची मुदत ठेव किंवा इतर गुंतवणूक र.रु. 20,000/-)",
    "आर्थिक क्षमतेबाबत र.रु. 25,000/- ची ऐपत असलेचे बँक सॉल्वन्सी/बँक गॅरेंटी/तहसीलदार यांचा ऐपत दाखला",
    "मागील दोन वर्षांची आयकर विवरणपत्रे ( सन 2024-25 व सन 2025-26 )",
    "आर्थिक शाश्वततेचे स्वघोषणापत्र",
  ];
  return (
    <div className="animate-fade-up">
      <Criteria>{CRITERIA[5]}</Criteria>
      <div className="rounded-lg border border-slate-200 p-5">
        <p className="mb-4 text-[14.5px] font-semibold text-[#1b5e3f]">
          आपले सरकार सेवा केंद्रासाठी लागणारी आवश्यक गुंतवणूक करण्याची व किमान ६ ते १२ महिने व्यवसाय चालविण्याची आर्थिकदृष्ट्या सक्षमता <span className="text-[#E0A106]">*</span>
        </p>
        <div className="space-y-3">
          {blocks.map((b, i) => {
            const yes = d[`fin_plan_${i}`] === "yes";
            return (
              <QuestionCard key={i} index={i} number={i + 1} title={b}
                headerRight={<YesNo name={`fin_plan_${i}`} onToggle={(v) => { if (v === "no") removeFields((k) => k === `fin_detail_${i}` || k === `fin_file_${i}`); }} />}>
                {i === 3 && (
                  <a href="/swaghoshnapatra-namuna.pdf" onClick={(e) => openPdf(e, "/swaghoshnapatra-namuna.pdf")} target="_blank" rel="noopener noreferrer" data-testid="swaghoshnapatra-namuna-link"
                    className="mb-3 inline-flex items-center gap-1.5 rounded-md border border-[#1b5e3f]/30 bg-[#1b5e3f]/5 px-3 py-1.5 text-[12.5px] font-semibold text-[#1b5e3f] hover:bg-[#1b5e3f]/10">
                    <Download className="h-3.5 w-3.5" /> स्वघोषणापत्राचा नमुना डाउनलोड करा
                  </a>
                )}
                {yes ? (
                  <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
                    <TextField label="तपशील" name={`fin_detail_${i}`} required errors={errors} />
                    <FileField label="पुरावा दस्तऐवज" name={`fin_file_${i}`} required errors={errors} compact />
                  </div>
                ) : (
                  <p className="text-[12.5px] text-slate-400">"आहे" निवडल्यास तपशील व पुरावा आवश्यक.</p>
                )}
              </QuestionCard>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Conditional sub-block wrapper
function CondBlock({ show, children }) {
  if (!show) return null;
  return (
    <div className="mt-4 rounded-lg border-l-4 border-[#1b5e3f] bg-slate-50 p-4 animate-fade-up">
      {children}
    </div>
  );
}

// ===================== STEP 6 : Experience =====================
export function Step6({ errors = {} }) {
  const { application, removeFields } = useApp();
  const d = application.data;

  return (
    <div className="animate-fade-up space-y-4">
      {/* Q1 - CSC */}
      <Fieldset legend="१. कॉमन सर्व्हिस सेंटर (CSC) क्षेत्रात काम केल्याबाबत अनुभव आहे / नाही">
        <YesNo name="cscExp" onToggle={(v) => { if (v === "no") removeFields((k) => k.startsWith("csc_")); }} />
        <CondBlock show={d.cscExp === "yes"}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <TextField label="CSC ID क्रमांक" name="csc_id" required errors={errors} />
            <TextField label="केंद्राचा पत्ता — गाव/शहर" name="csc_addr" required errors={errors} />
            <TextField label="वॉर्ड क्रमांक (शहरी)" name="csc_ward" errors={errors} />
            <TextField label="ग्रामपंचायत/नगरपंचायत/नगरपरिषद (पर्यायी)" name="csc_body" errors={errors} />
            <TextField label="तालुका" name="csc_taluka" required errors={errors} />
            <TextField label="जिल्हा" name="csc_district" readOnly errors={errors} />
            <TextField label="पिनकोड" name="csc_pin" required maxLength={6} errors={errors} />
            <FileField label="CSC प्रमाणपत्र / पुरावा" name="csc_file" required errors={errors} compact />
          </div>
        </CondBlock>
      </Fieldset>

      {/* Q2 - Banking rep */}
      <Fieldset legend="२. बँकिंग प्रतिनिधी क्षेत्रात काम केल्याबाबत अनुभव आहे / नाही">
        <YesNo name="bankRep" yesLabel="आहेत" noLabel="नाहीत" onToggle={(v) => { if (v === "no") removeFields((k) => k.startsWith("bk_")); }} />
        <CondBlock show={d.bankRep === "yes"}>
          <div className="mb-4">
            <TextareaField label="बँक ग्राहक सेवा केंद्राचा तपशील" name="bk_details" rows={2} errors={errors} />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextField label="बँक ग्राहक सेवा केंद्राचा ID क्रमांक" name="bk_id" required errors={errors} />
            <TextField label="केंद्राचा पत्ता — गाव/शहर" name="bk_addr" required errors={errors} />
            <TextField label="ग्रामपंचायत/नगरपंचायत/नगरपरिषद" name="bk_body" errors={errors} />
            <TextField label="तालुका" name="bk_taluka" required errors={errors} />
            <TextField label="जिल्हा" name="bk_district" readOnly errors={errors} />
            <TextField label="पिनकोड" name="bk_pin" required maxLength={6} errors={errors} />
            <FileField label="बँकिंग प्रतिनिधी प्रमाणपत्र / पुरावा" name="bk_file" required errors={errors} compact />
          </div>
        </CondBlock>
      </Fieldset>

      {/* Q3 - eGov */}
      <Fieldset legend="३. ई-गव्हर्नन्स प्रकल्प ( वर्क ऑर्डरसह) शासकीय सेवा केंद्र कराराचा अनुभव ( वर्क ऑर्डरसह) आहे / नाही">
        <YesNo name="egovExp" onToggle={(v) => { if (v === "no") removeFields((k) => k.startsWith("eg_")); }} />
        <CondBlock show={d.egovExp === "yes"}>
          <div className="mb-4">
            <TextareaField label="अनुभवाचा तपशील (नियुक्ती आदेश / अनुभव प्रमाणपत्र)" name="eg_details" rows={4} required errors={errors} />
          </div>
          <FileField label="ई-गव्हर्नन्स अनुभव प्रमाणपत्र / पुरावा" name="eg_file" required errors={errors} compact />
        </CondBlock>
      </Fieldset>

      {/* Q4 - previous center */}
      <Fieldset legend="४. महा-ई सेवा केंद्र क्षेत्रात काम केल्याबाबत अनुभव आहे / नाही">
        <YesNo name="prevCenter" onToggle={(v) => { if (v === "no") removeFields((k) => k.startsWith("pc_")); }} />
        <CondBlock show={d.prevCenter === "yes"}>
          <div className="mb-4">
            <TextareaField label="केंद्राचे नाव व पत्ता" name="pc_addr" rows={2} required errors={errors} />
          </div>
          <div className="mb-4">
            <TextField label="महा ई सेवा केंद्र ID" name="pc_id" required errors={errors} />
          </div>
          <FileField label="महा-ई सेवा केंद्र अनुभव प्रमाणपत्र / पुरावा" name="pc_file" required errors={errors} compact />
        </CondBlock>
      </Fieldset>
    </div>
  );
}
